import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';
import Profile from './Profile';
import Login from './Login';
import Signup from './Signup';
import ManageCredits from './ManageCredits';
import BetaPending from './BetaPending';
import BetaRequest from './BetaRequest';
import DownloadPage from './DownloadPage';
import Home from './Home';
import ReportBug from './ReportBug';
import AdminModeration from './AdminModeration';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import FeaturesOfLaunchway from './FeaturesOfLaunchway';
import { getApiEndpoint } from './config';

// Determines the correct destination by verifying the token against the DB.
// localStorage is only used as a quick "no token → skip the API call" shortcut.
function HomeRouteGuard() {
  // 'loading' | 'marketing' | 'request' | 'pending' | 'home'
  const [view, setView] = useState('loading');

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setView('marketing');
      return;
    }

    fetch(getApiEndpoint('/api/auth/verify'), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (!data.success || !data.user) {
          // Token invalid / expired — clear stale data
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setView('marketing');
          return;
        }

        // Keep localStorage in sync with the live DB value
        const fresh = { ...data.user };
        localStorage.setItem('user', JSON.stringify(fresh));

        if (!fresh.beta_access_approved) {
          // Route to re-request page if they haven't submitted a request yet
          // (e.g. after a rejection clears beta_access_requested)
          if (fresh.beta_access_requested) {
            setView('pending');
          } else {
            setView('request');
          }
        } else {
          setView('home');
        }
      })
      .catch(() => {
        // Network error — fall back to cached localStorage value so the app
        // still works offline, but don't give access to unapproved users.
        const raw = localStorage.getItem('user');
        try {
          const cached = JSON.parse(raw);
          if (cached?.beta_access_approved) {
            setView('home');
          } else {
            setView('marketing');
          }
        } catch {
          setView('marketing');
        }
      });
  }, []);

  if (view === 'loading') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0a0a0f', color: '#06b6d4', fontSize: '1rem',
        fontFamily: "'Space Mono', monospace", letterSpacing: '0.05em'
      }}>
        Verifying session…
      </div>
    );
  }
  if (view === 'request') return <Navigate to="/beta-request" replace />;
  if (view === 'pending') return <Navigate to="/beta-pending" replace />;
  if (view === 'marketing') return <FeaturesOfLaunchway />;
  return <Home />;
}

function App() {
  return (
    <>
    <BrowserRouter>
    <Routes>
      <Route path='/' element={<HomeRouteGuard />} />
      <Route path='/login' element={<Login />} />
      <Route path='/signup' element={<Signup />} />
      <Route path='/profile' element={<Profile />} />
      <Route path="/manage-credits" element={<ManageCredits />} />
      <Route path="/beta-pending" element={<BetaPending />} />
      <Route path="/beta-request" element={<BetaRequest />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/report-bug" element={<ReportBug />} />
      <Route path="/admin/moderation" element={<AdminModeration />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/features-of-launchway" element={<Navigate to="/" replace />} />
    </Routes>
    </BrowserRouter>
    </>
  );
}

export default App;